import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormApi } from 'final-form';
import { GraphQLError, GraphQLFormattedError } from 'graphql';
import { snackbar } from '@occc-app/design-system';
import {
  useGetSurveysByWalletId,
  useGetWallet,
} from '@occc-app/dataAccessLayer/steeringWallet/queries';
import { useGetProductGroupById } from '@occc-app/dataAccessLayer/productGroup/queries';
import { useChainId } from '@occc-app/Chain/hooks';

import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';

import { EditWalletPage } from './EditWalletPage';
import { getIncentivizeResponsesInitialValues } from './utils';

import {
  useUpdateWallet,
  useCreateSurvey,
  useUpdateSurvey,
  useUpdateSurveyOffer,
  useDeleteSurveyOffer,
  useCreateSurveyOffer,
} from '../hooks';
import { WalletFormValues } from '../types';
import { accumulateErrors, mapWalletData, routeCreator } from '../utils';

export const EditWalletPageContainer: React.FC = () => {
  const { id: walletId = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const chainId = useChainId();
  const isUserPermittedToEditWallet = useIsUserPermittedTo(Feature.WALLET_EDIT);

  const createSurvey = useCreateSurvey();
  const updateSurvey = useUpdateSurvey();
  const updateWallet = useUpdateWallet();
  const { createSurveyOffer } = useCreateSurveyOffer();
  const { updateSurveyOffer } = useUpdateSurveyOffer();
  const { deleteSurveyOffer } = useDeleteSurveyOffer();

  const walletResult = useGetWallet(walletId);
  const surveyResult = useGetSurveysByWalletId(walletId);

  const productGroupId: string | undefined | null =
    walletResult.data?.steeringWallet.configuration.productGroupId;
  const productGroupResult = useGetProductGroupById(productGroupId, chainId);

  const walletData = useMemo(
    () => mapWalletData(walletResult, surveyResult, productGroupResult),
    [walletResult, surveyResult, productGroupResult],
  );

  const { survey, wallet, productGroup } = walletData;
  const walletName = wallet.data?.name;
  const surveyUuid: string | undefined = survey.data?.uuid;

  const initialValues: Partial<WalletFormValues> = useMemo(() => {
    if (survey?.data?.__typename !== 'WalletSteeringSurvey') {
      return {};
    }

    const surveyConfiguration = survey.data?.configuration;

    return {
      name: walletName,
      question: surveyConfiguration?.questionText || undefined,
      rangeMin: surveyConfiguration?.minUnits ?? undefined,
      rangeMax: surveyConfiguration?.maxUnits ?? undefined,
      period: wallet.data?.configuration.basis || undefined,
      countWalletBy: wallet.data?.configuration.unitOfMeasure || undefined,
      productGroup: productGroup.data || undefined,
      incentivizeResponses: getIncentivizeResponsesInitialValues(survey.data.award),
    };
  }, [productGroup, survey, wallet, walletName]);

  const onSubmit = React.useCallback(
    async (
      values: WalletFormValues,
      form: FormApi<WalletFormValues, Partial<WalletFormValues>>,
    ) => {
      let errors: Array<GraphQLError | GraphQLFormattedError> = [];
      const dirtyFields = form.getState().dirtyFields;

      const isWalletUpdated = dirtyFields.name;
      const isSurveyUpdated = dirtyFields.question || dirtyFields.rangeMin || dirtyFields.rangeMax;
      const isSurveyExist = !!surveyUuid;
      const isSurveyAwardExist = !!survey.data?.award;
      const isIncentivizeResponsesChanged = !!(
        dirtyFields['incentivizeResponses.isEnabled'] ||
        dirtyFields['incentivizeResponses.awardPoints.points'] ||
        dirtyFields['incentivizeResponses.awardVouchers.voucheredOffer']
      );

      const isIncentivizeResponsesUpdated =
        isSurveyExist && isIncentivizeResponsesChanged && values.incentivizeResponses.isEnabled;
      const isIncentivizeResponsesRemoved =
        isSurveyExist && isIncentivizeResponsesChanged && !values.incentivizeResponses.isEnabled;

      let newWallet = null;
      if (isWalletUpdated) {
        const { errors: updateWalletErrors, data } = await updateWallet(walletId, values);
        errors = accumulateErrors(errors, updateWalletErrors);
        if (data?.steeringWalletUpdate) {
          newWallet = data.steeringWalletUpdate;
        }
      }

      // If there is a survey associated with this wallet we have to update it.
      if (isSurveyExist && isSurveyUpdated) {
        const { errors: updateSurveyErrors } = await updateSurvey(surveyUuid, values);
        errors = accumulateErrors(errors, updateSurveyErrors);
      }

      // If there is no survey associated with this wallet we have to create it.
      if (!isSurveyExist) {
        const { errors: createSurveyErrors } = await createSurvey(walletId, values);
        errors = accumulateErrors(errors, createSurveyErrors);
      }

      // If there is an award associated with survey we have to update it
      if (isSurveyAwardExist && isIncentivizeResponsesUpdated) {
        const { errors: updateSurveyOfferErrors } = await updateSurveyOffer({
          surveyUuid,
          values: values.incentivizeResponses,
        });
        errors = accumulateErrors(errors, updateSurveyOfferErrors);
      }

      // If there is no an award associated with survey and incentive form values changed we have to create it
      if (!isSurveyAwardExist && isSurveyExist && isIncentivizeResponsesUpdated) {
        const { errors: createSurveyOfferErrors } = await createSurveyOffer({
          surveyUuid,
          values: values.incentivizeResponses,
        });
        errors = accumulateErrors(errors, createSurveyOfferErrors);
      }

      if (isIncentivizeResponsesRemoved) {
        const { errors: deleteSurveyOfferErrors } = await deleteSurveyOffer(surveyUuid);
        errors = accumulateErrors(errors, deleteSurveyOfferErrors);
      }

      const isSubmittedWithNoErrors = errors.length === 0;

      if (isSubmittedWithNoErrors) {
        snackbar.success(`${newWallet?.name || walletName} has been successfully updated.`);
        navigate(routeCreator.list());
      } else {
        snackbar.error('Something went wrong. Please try again.');
      }
    },
    [
      surveyUuid,
      navigate,
      updateWallet,
      createSurvey,
      updateSurvey,
      createSurveyOffer,
      updateSurveyOffer,
      deleteSurveyOffer,
      walletId,
      walletName,
      survey.data?.award,
    ],
  );

  return (
    <EditWalletPage
      isUserPermittedToEditWallet={isUserPermittedToEditWallet}
      walletData={walletData}
      onSubmit={onSubmit}
      initialValues={initialValues}
    />
  );
};
